import { ComponentType } from 'react';

import {
  Box as Flex,
  Chip as Tag,
  Typography as Text,
} from '@material-ui/core';

import capitalize from 'lodash/capitalize';

import {
  StatusOK,
  StatusPending,
  StatusRunning,
  StatusWarning,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';
import {
  FlagsAdminV1SegmentState,
  Maybe,
  isType,
} from '@spotify/confidence-graphql';
import { useSetResolvedTitle } from '@spotify/plugin-confidence-projects-react';
import { useSegment } from '@spotify/plugin-confidence-targeting-react';

import { PreviewShell } from './components';

interface SegmentArtifactPreviewProps {
  resourceName: string;
}

const STATE_DOTS: Record<FlagsAdminV1SegmentState, ComponentType> = {
  [FlagsAdminV1SegmentState.Allocated]: StatusOK,
  [FlagsAdminV1SegmentState.Archived]: StatusPending,
  [FlagsAdminV1SegmentState.Unallocated]: StatusRunning,
  [FlagsAdminV1SegmentState.Ok]: StatusOK,
};

const StateIndicator = ({
  state,
}: {
  state?: Maybe<FlagsAdminV1SegmentState>;
}) => {
  const Dot = state ? STATE_DOTS[state] : StatusWarning;
  return (
    <Flex
      display="flex"
      alignItems="center"
      style={{
        gap: 'var(--bui-space-2)',
      }}
    >
      <Dot />
      <Text variant="body2" color="textSecondary">
        {capitalize(state ?? 'unknown')}
      </Text>
    </Flex>
  );
};

const formatAllocation = (proportion: unknown): string | undefined => {
  if (typeof proportion !== 'string' && typeof proportion !== 'number') {
    return undefined;
  }
  const value = Number(proportion);
  if (!Number.isFinite(value)) return undefined;
  return `${Math.round(value * 100)}%`;
};

export const SegmentArtifactPreview = ({
  resourceName,
}: SegmentArtifactPreviewProps) => {
  const { segment, isLoading, error } = useSegment({ name: resourceName });

  const linkedFlag = isType(segment?.flag, 'FlagsAdminV1Flag')
    ? segment.flag
    : undefined;
  const previewError =
    error ?? (!isLoading && !segment ? 'not-found' : undefined);

  useSetResolvedTitle(
    segment?.displayName || extractLastNameComponent(resourceName),
  );

  const allocation = formatAllocation(segment?.allocation?.proportion?.value);

  return (
    <PreviewShell loading={isLoading} error={previewError}>
      {segment && (
        <Flex
          display="flex"
          flexDirection="column"
          style={{
            gap: 'var(--bui-space-2)',
          }}
        >
          <Flex
            display="flex"
            flexDirection="column"
            style={{
              gap: 'var(--bui-space-1)',
            }}
          >
            <Text
              variant="body1"
              style={{
                fontWeight: 'bold',
              }}
            >
              {segment.displayName ||
                extractLastNameComponent(segment.name) ||
                segment.name}
            </Text>
            <StateIndicator state={segment.state} />
          </Flex>
          {segment.description && (
            <Text variant="body2">{segment.description}</Text>
          )}
          {allocation && (
            <Flex
              display="flex"
              alignItems="center"
              style={{
                gap: 'var(--bui-space-2)',
              }}
            >
              <Text variant="body2">Allocation:</Text>
              <Text variant="body2" color="textSecondary">
                {allocation}
              </Text>
            </Flex>
          )}
          {linkedFlag &&
            (() => {
              const flagLabel =
                linkedFlag.displayName ||
                extractLastNameComponent(linkedFlag.name) ||
                linkedFlag.name;
              return (
                <Flex
                  display="flex"
                  alignItems="center"
                  style={{
                    gap: 'var(--bui-space-2)',
                  }}
                >
                  <Text variant="body2">Flag:</Text>
                  <Flex
                    display="flex"
                    flexWrap="wrap"
                    style={{
                      gap: 'var(--bui-space-2)',
                    }}
                    aria-label="Flag"
                  >
                    <Tag id={linkedFlag.name} label={flagLabel} size="small" />
                  </Flex>
                </Flex>
              );
            })()}
        </Flex>
      )}
    </PreviewShell>
  );
};
